import React from 'react';

// F-12 — Memory Care "coming soon" placeholder + interest sensor.
// Copy is founder-ratified (2026-07-13), em-dash-swapped to comply with DESIGN_CONSTITUTION §5
// (founder ratified the swap live: headline "Memory Care is coming soon.", button "Let me know
// when it's ready"; body + after-tap unchanged).
//
// Renders where a Memory Care doorway used to be. `variant`:
//   'tile'   — inline card (chooser tertiary tile)
//   'inline' — inline block (StWritePage crosslink)
//   'modal'  — overlay (subject-card / switcher taps), gated by `open`
// The "I'm interested" button fires once per user (localStorage) and calls `onInterest`
// (App wires that to the reused /feedback→hello@ contact path + GA4 memory_care_interest).

const HEADLINE = 'Memory Care is coming soon.';
const BODY = "Capturing a loved one's stories deserves extra care. We're getting it right first.";
const CTA = "Let me know when it's ready";
const THANKS = 'Thank you. We\'ll let you know.';
const LS_KEY = 'tb_mc_interest';

const LAV = '#9b8faa';

function InterestButton({ onInterest }) {
  const [done, setDone] = React.useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1'; } catch (e) { return false; }
  });
  const [sending, setSending] = React.useState(false);

  const tap = async () => {
    if (done || sending) return;
    setSending(true);
    let ok = true;
    try { ok = (await (onInterest ? onInterest() : Promise.resolve(true))) !== false; }
    catch (e) { ok = false; }
    if (ok) {
      try { localStorage.setItem(LS_KEY, '1'); } catch (e) { /* private mode */ }
      setDone(true);
    }
    setSending(false);
  };

  if (done) {
    return (
      <div data-testid="mc-interest-thanks" style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', marginTop: 4 }}>
        {THANKS}
      </div>
    );
  }
  return (
    <button
      data-testid="mc-interest-btn"
      onClick={tap}
      disabled={sending}
      style={{
        minHeight: 44, padding: '12px 20px', background: LAV, color: '#fff', border: 'none',
        borderRadius: 12, cursor: sending ? 'default' : 'pointer', fontFamily: "'Lora', serif",
        fontSize: '1rem', fontWeight: 500, opacity: sending ? 0.7 : 1, marginTop: 6,
      }}>
      {sending ? 'Sending…' : CTA}
    </button>
  );
}

function Card({ onInterest }) {
  return (
    <div data-testid="mc-coming-soon" style={{
      background: 'rgba(155,143,170,0.06)', border: `1px dashed ${LAV}`, borderRadius: 14,
      padding: '18px 20px', textAlign: 'center',
    }}>
      <div style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>
        {HEADLINE}
      </div>
      <div style={{ fontSize: '0.98rem', color: '#3c4653', lineHeight: 1.5, maxWidth: 420, margin: '0 auto 4px' }}>
        {BODY}
      </div>
      <InterestButton onInterest={onInterest} />
    </div>
  );
}

export default function MemoryCareComingSoon({ variant = 'tile', open = false, onClose, onInterest }) {
  if (variant === 'modal') {
    if (!open) return null;
    return (
      <div
        data-testid="mc-coming-soon-modal"
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, padding: '10px 10px 16px', maxWidth: 440, width: '100%' }}>
          <div style={{ textAlign: 'right' }}>
            <button onClick={onClose} aria-label="Close" style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', fontSize: '1.3rem', color: '#9a8570', cursor: 'pointer' }}>×</button>
          </div>
          <div style={{ padding: '0 14px' }}>
            <Card onInterest={onInterest} />
          </div>
        </div>
      </div>
    );
  }
  // 'tile' and 'inline' both render the card in place; 'inline' just constrains width/centering.
  if (variant === 'inline') {
    return (
      <div style={{ maxWidth: 460, margin: '0 auto' }}>
        <Card onInterest={onInterest} />
      </div>
    );
  }
  return <Card onInterest={onInterest} />;
}
